import React, { useState, useMemo, useRef, useEffect } from 'react';

const TrendsPropReport = ({ data, teams, market, loading, oddsData, onPlayerClick, isPastYear = false, sport = 'nba', currentTeamByPlayerId: externalRosterMap = null }) => {
  // Prefer the externally-supplied roster (from /today payload — complete current roster).
  // Fall back to deriving from odds data, which is market-scoped and may miss players.
  const oddsRosterMap = useMemo(() => {
    const map = new Map();
    if (!oddsData?.players) return map;
    oddsData.players.forEach(p => {
      const pid = String(p.playerId);
      const team = p.team || p.teamId;
      if (pid && team && !map.has(pid)) {
        map.set(pid, team);
      }
    });
    return map;
  }, [oddsData]);
  const currentTeamByPlayerId = (externalRosterMap && externalRosterMap.size > 0) ? externalRosterMap : oddsRosterMap;
  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedSubLine, setSelectedSubLine] = useState(null);
  const [viewMode, setViewMode] = useState('tiles'); // 'tiles' or 'grid'
  const [showAlts, setShowAlts] = useState(true); // When false, only show O/U lines from odds
  const [showDefense, setShowDefense] = useState(false);
  const [defenseData, setDefenseData] = useState({});
  const [defenseLoading, setDefenseLoading] = useState(false);

  // Parse teams from "MIA @ WSH" format
  const parseTeams = () => {
    if (!teams) return { away: null, home: null };
    const parts = teams.split('@').map(t => t.trim());
    return { away: parts[0] || null, home: parts[1] || null };
  };

  const { away: awayTeam, home: homeTeam } = parseTeams();

  // Map player market to defense key(s) — arrays for combo markets
  const defenseKeyMap = {
    'player_points': ['defense_points'],
    'player_rebounds': ['defense_rebounds'],
    'player_assists': ['defense_assists'],
    'player_threes': ['defense_threes'],
    'player_double_double': ['defense_double_doubles'],
    'player_points_rebounds_assists': ['defense_points_rebounds_assists'],
    'player_points_rebounds': ['defense_points_rebounds'],
    'player_points_assists': ['defense_points_assists'],
    'player_rebounds_assists': ['defense_rebounds_assists'],
  };

  // Combine defense stats across multiple keys for combo markets
  // Summary uses primary stat only (e.g. defense_points for PRA)
  // Last 5 games combine topPlayers across component stats
  const combineDefenseStats = (teamDef, keys) => {
    if (!teamDef || !keys || keys.length === 0) return null;

    // Single key — return as-is
    if (keys.length === 1) {
      return teamDef[keys[0]] || null;
    }

    // Multiple keys — use primary stat for summary, combine games for last 5
    const primaryData = teamDef[keys[0]];
    if (!primaryData) return null;

    const allMarketData = keys.map(k => teamDef[k]).filter(Boolean);

    // Combine games: merge topPlayers across stats by gameId and playerId
    const gamesByGameId = {};
    allMarketData.forEach(m => {
      (m.games || []).forEach(g => {
        if (!gamesByGameId[g.gameId]) {
          gamesByGameId[g.gameId] = {
            gameId: g.gameId,
            gameDateKey: g.gameDateKey,
            isHome: g.isHome,
            isBackToBack: g.isBackToBack,
            opponent: g.opponent,
            value: 0,
            playerTotals: {}, // playerId -> { playerName, value }
          };
        }
        const entry = gamesByGameId[g.gameId];
        entry.value += g.value || 0;

        // Accumulate topPlayer values by playerId across stats
        if (g.topPlayer && g.topPlayer.playerId) {
          const pid = g.topPlayer.playerId;
          if (!entry.playerTotals[pid]) {
            entry.playerTotals[pid] = { playerName: g.topPlayer.playerName, value: 0 };
          }
          entry.playerTotals[pid].value += g.topPlayer.value || 0;
        }
      });
    });

    // For each game, pick the player with the highest combined value
    const combinedGames = Object.values(gamesByGameId)
      .map(g => {
        const players = Object.values(g.playerTotals);
        const bestPlayer = players.length > 0
          ? players.reduce((best, p) => p.value > best.value ? p : best, players[0])
          : null;
        return {
          gameId: g.gameId,
          gameDateKey: g.gameDateKey,
          isHome: g.isHome,
          isBackToBack: g.isBackToBack,
          opponent: g.opponent,
          value: g.value,
          topPlayer: bestPlayer,
        };
      })
      .sort((a, b) => a.gameDateKey.localeCompare(b.gameDateKey));

    return {
      overall: primaryData.overall || {},
      home: primaryData.home || {},
      away: primaryData.away || {},
      back_to_back: primaryData.back_to_back || {},
      games: combinedGames,
    };
  };

  // Fetch defense data when toggle is on
  useEffect(() => {
    if (!showDefense || !awayTeam || !homeTeam) return;

    const fetchDefense = async (team) => {
      try {
        const response = await fetch(`/api/stats/nba/team/${team}`, {
          credentials: 'include',
        });
        if (response.ok) {
          const result = await response.json();
          return result.data || null;
        }
      } catch (err) {
        console.error(`Error fetching defense for ${team}:`, err);
      }
      return null;
    };

    setDefenseLoading(true);
    Promise.all([fetchDefense(awayTeam), fetchDefense(homeTeam)])
      .then(([awayData, homeData]) => {
        setDefenseData({ [awayTeam]: awayData, [homeTeam]: homeData });
      })
      .finally(() => setDefenseLoading(false));
  }, [showDefense, awayTeam, homeTeam]);

  // Market display config - defaultLine is middle-of-the-road value
  const marketConfig = {
    'player_points': { label: 'Points', abbrev: 'Pts', defaultLine: 15 },
    'player_rebounds': { label: 'Rebounds', abbrev: 'Reb', defaultLine: 5 },
    'player_assists': { label: 'Assists', abbrev: 'Ast', defaultLine: 4 },
    'player_threes': { label: 'Threes', abbrev: '3s', defaultLine: 2 },
    'player_steals': { label: 'Steals', abbrev: 'Stl', defaultLine: 1 },
    'player_blocks': { label: 'Blocks', abbrev: 'Blk', defaultLine: 1 },
    'player_turnovers': { label: 'Turnovers', abbrev: 'TO', defaultLine: 2 },
    'player_points_rebounds_assists': { label: 'PRA', abbrev: 'PRA', defaultLine: 25 },
    'player_points_rebounds': { label: 'Pts+Reb', abbrev: 'P+R', defaultLine: 20 },
    'player_points_assists': { label: 'Pts+Ast', abbrev: 'P+A', defaultLine: 20 },
    'player_rebounds_assists': { label: 'Reb+Ast', abbrev: 'R+A', defaultLine: 10 }
  };

  const config = marketConfig[market] || { label: market, abbrev: '', defaultLine: 10 };

  // Extract main O/U lines from odds data - maps playerId to array of main lines
  const oddsLinesByPlayer = useMemo(() => {
    if (!oddsData?.players) return {};

    const linesByPlayer = {};
    oddsData.players.forEach(player => {
      // Skip if not available
      if (player.isAvailable === false) return;
      // Only include main lines (not alternates)
      if (player.isMain !== true && player.isAlternate !== false) return;

      const playerId = String(player.playerId);
      const point = player.point;
      if (playerId && point !== null && point !== undefined) {
        const pointNum = parseFloat(point);
        if (!isNaN(pointNum) && pointNum > 0) {
          if (!linesByPlayer[playerId]) {
            linesByPlayer[playerId] = new Set();
          }
          // Store ceil value - e.g. 2.5 line means you need 3+ to hit over
          linesByPlayer[playerId].add(Math.ceil(pointNum));
        }
      }
    });

    // Convert sets to sorted arrays
    Object.keys(linesByPlayer).forEach(playerId => {
      linesByPlayer[playerId] = Array.from(linesByPlayer[playerId]).sort((a, b) => a - b);
    });

    return linesByPlayer;
  }, [oddsData]);

  // Get all unique O/U lines across all players
  const allOddsLines = useMemo(() => {
    const allLines = new Set();
    Object.values(oddsLinesByPlayer).forEach(lines => {
      lines.forEach(line => allLines.add(line));
    });
    return Array.from(allLines).sort((a, b) => a - b);
  }, [oddsLinesByPlayer]);

  // Get all available lines from the data
  const availableLines = useMemo(() => {
    if (!data?.players) return [];
    const linesSet = new Set();

    data.players.forEach(player => {
      const marketData = player[market];
      if (marketData?.season?.milestones) {
        Object.keys(marketData.season.milestones).forEach(line => {
          const lineNum = parseInt(line, 10);
          if (!isNaN(lineNum)) linesSet.add(lineNum);
        });
      }
    });

    return Array.from(linesSet).sort((a, b) => a - b);
  }, [data, market]);

  // Track previous market to detect actual market changes
  const prevMarketRef = useRef(market);

  // Reset line when market changes
  useEffect(() => {
    if (prevMarketRef.current !== market) {
      prevMarketRef.current = market;
      setSelectedLine(null);
      setSelectedSubLine(null);
    }
  }, [market]);

  // Set default line when availableLines loads or selectedLine is null
  useEffect(() => {
    if (availableLines.length > 0 && selectedLine === null) {
      const defaultLine = config.defaultLine;
      const closest = availableLines.reduce((prev, curr) =>
        Math.abs(curr - defaultLine) < Math.abs(prev - defaultLine) ? curr : prev
      );
      setSelectedLine(closest);
    }
  }, [availableLines, config.defaultLine, selectedLine]);

  // When main line changes, reset sub-line and set default for points
  // Markets that have sub-line toggles
  const marketsWithSubLines = [
    'player_points',
    'player_points_rebounds_assists',
    'player_points_rebounds',
    'player_points_assists',
    'player_rebounds_assists'
  ];

  React.useEffect(() => {
    if (marketsWithSubLines.includes(market) && selectedLine !== null) {
      setSelectedSubLine(selectedLine); // Default to first sub-line
    } else {
      setSelectedSubLine(null);
    }
  }, [selectedLine, market]);

  // Helper to get stat value for a specific line
  const getLineStats = (player, line) => {
    const marketData = player[market];
    if (!marketData) return null;

    const lineStr = String(line);
    const season = marketData.season?.milestones?.[lineStr];
    const last5 = marketData.last5?.milestones?.[lineStr];
    const last10 = marketData.last10?.milestones?.[lineStr];
    const last20 = marketData.last20?.milestones?.[lineStr];

    return {
      seasonPct: season?.pct ?? null,
      seasonHit: season?.hit ?? 0,
      gamesPlayed: marketData.season?.gamesPlayed ?? 0,
      last5Pct: last5?.pct ?? null,
      last10Pct: last10?.pct ?? null,
      last20Pct: last20?.pct ?? null,
      max: marketData.max ?? 0
    };
  };

  const styles = {
    container: {
      width: '100%',
      color: '#fff',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      height: '100%'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: '0.5rem'
    },
    headerLeft: {
      flex: '0 0 auto'
    },
    headerCenter: {
      display: 'flex',
      gap: '0.25rem',
      justifyContent: 'center'
    },
    headerRight: {
      flex: '0 0 auto',
      visibility: 'hidden'  // For balance
    },
    title: {
      fontSize: '1.2em',
      fontWeight: '600',
      fontFamily: "'Oswald', sans-serif",
      margin: 0,
      textTransform: 'uppercase',
      whiteSpace: 'nowrap',
      color: '#ffc107'
    },
    toggleContainer: {
      display: 'flex',
      gap: '0.25rem',
      justifyContent: 'center',
      marginBottom: '0.5rem',
      flexWrap: 'wrap'
    },
    lineToggle: {
      background: '#333',
      color: '#888',
      border: '1px solid #444',
      padding: '0.2rem 0.4rem',
      fontSize: '0.65rem',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      fontWeight: '600',
      minWidth: '28px'
    },
    lineToggleActive: {
      background: '#f33',
      color: 'white',
      borderColor: '#f33'
    },
    columnsContainer: {
      display: 'flex',
      gap: '0.5rem',
      flex: 1,
      overflow: 'hidden'
    },
    teamColumn: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden'
    },
    teamHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.4rem',
      background: '#222',
      borderRadius: '0.25rem',
      marginBottom: '0.5rem'
    },
    teamLogo: {
      width: '24px',
      height: '24px',
      objectFit: 'contain'
    },
    teamName: {
      fontSize: '0.85rem',
      fontWeight: 'bold'
    },
    playersList: {
      flex: 1,
      overflowY: 'auto'
    },
    playerRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.35rem',
      borderBottom: '1px solid #333',
      background: '#1a1a1a',
      borderRadius: '0.35rem',
      marginBottom: '0.35rem'
    },
    rank: {
      width: '18px',
      fontSize: '0.75rem',
      color: '#888',
      textAlign: 'center',
      flexShrink: 0
    },
    headshot: {
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      objectFit: 'cover',
      flexShrink: 0
    },
    playerInfo: {
      flex: 1,
      minWidth: 0
    },
    playerNameLine: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.3rem'
    },
    playerName: {
      fontSize: '0.75rem',
      fontWeight: 'bold',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      textAlign: 'left'
    },
    lineLabel: {
      fontSize: '0.65rem',
      color: '#f33',
      fontWeight: 'bold',
      flexShrink: 0
    },
    statsLine: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.3rem',
      marginTop: '0.1rem',
      flexWrap: 'wrap'
    },
    statBadge: {
      fontSize: '0.6rem',
      padding: '0.1rem 0.25rem',
      borderRadius: '0.15rem',
      background: '#333'
    },
    statTierBlue: {
      background: '#3B82F6',
      color: 'white'
    },
    statTierYellow: {
      background: '#F59E0B',
      color: '#1E40AF'
    },
    statTierGreen: {
      background: 'green',
      color: 'yellow'
    },
    statNeutral: {
      background: '#333',
      color: '#aaa'
    },
    loading: {
      textAlign: 'center',
      padding: '2rem',
      color: '#888'
    },
    noData: {
      textAlign: 'center',
      padding: '0.5rem',
      color: '#666',
      fontSize: '0.75rem'
    },
    altsToggle: {
      background: '#333',
      color: '#888',
      border: '1px solid #444',
      padding: '0.2rem 0.5rem',
      fontSize: '0.65rem',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      fontWeight: '600',
      marginLeft: 'auto'
    },
    altsToggleActive: {
      background: '#2ecc71',
      color: 'white',
      borderColor: '#2ecc71'
    },
    viewToggle: {
      background: '#333',
      color: '#888',
      border: '1px solid #444',
      padding: '0.2rem 0.5rem',
      fontSize: '0.65rem',
      borderRadius: '0.25rem',
      cursor: 'pointer',
      fontWeight: '600'
    },
    viewToggleActive: {
      background: '#3498db',
      color: 'white',
      borderColor: '#3498db'
    },
    gridContainer: {
      flex: 1,
      overflowY: 'auto'
    },
    gridTable: {
      width: '100%',
      borderCollapse: 'collapse',
      fontSize: '0.7rem'
    },
    gridHeader: {
      background: '#222',
      position: 'sticky',
      top: 0
    },
    gridHeaderCell: {
      padding: '0.4rem 0.25rem',
      textAlign: 'center',
      fontWeight: 'bold',
      borderBottom: '1px solid #444'
    },
    gridRow: {
      borderBottom: '1px solid #333'
    },
    gridRowAway: {
      background: '#1a1a1a'
    },
    gridRowHome: {
      background: '#252525'
    },
    gridCell: {
      padding: '0.3rem 0.25rem',
      textAlign: 'center'
    },
    gridCellTeam: {
      fontWeight: 'bold',
      color: '#888'
    },
    gridCellPlayer: {
      textAlign: 'left',
      fontWeight: 'bold'
    }
  };

  if (loading) {
    return <div style={styles.loading}>Loading prop data...</div>;
  }

  const playersData = data?.players || [];

  // Get stat badge style based on percentage (tier coloring)
  const getStatStyle = (pct) => {
    if (pct === null) return styles.statNeutral;
    if (pct >= 71) return { ...styles.statBadge, ...styles.statTierGreen };
    if (pct >= 61) return { ...styles.statBadge, ...styles.statTierYellow };
    if (pct >= 51) return { ...styles.statBadge, ...styles.statTierBlue };
    return { ...styles.statBadge, ...styles.statNeutral };
  };

  // Check if player has any odds data
  const playerHasOdds = (playerId) => {
    const lines = oddsLinesByPlayer[String(playerId)] || [];
    return lines.length > 0;
  };

  // Render a single player row
  const renderPlayerRow = (player, index) => {
    if (activeLine === null) return null;

    const stats = getLineStats(player, activeLine);
    if (!stats || stats.seasonPct === null) return null;

    const headshotUrl = player.playerId
      ? `https://a.espncdn.com/combiner/i?img=/i/headshots/${sport}/players/full/${player.playerId}.png&w=96&h=70&cb=1`
      : null;

    const hasOdds = playerHasOdds(player.playerId);
    const rowStyle = {
      ...styles.playerRow,
      ...(hasOdds ? {} : { opacity: 0.25 })
    };

    return (
      <div key={player.playerId || index} style={rowStyle}>
        <div style={styles.rank}>{index + 1}</div>
        {headshotUrl && (
          <img
            style={styles.headshot}
            src={headshotUrl}
            alt={player.playerName}
            onError={(e) => e.target.style.display = 'none'}
          />
        )}
        <div style={styles.playerInfo}>
          <div style={{ ...styles.playerName, ...(onPlayerClick ? { cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#6b7280' } : {}) }} onClick={onPlayerClick ? () => onPlayerClick(player.playerId, player.playerName, player.team) : undefined}>
            {player.playerName}
            {isPastYear && player.team && currentTeamByPlayerId.get(String(player.playerId)) && player.team !== currentTeamByPlayerId.get(String(player.playerId)) && (
              <span style={{ color: '#9ca3af', fontWeight: 'normal', marginLeft: '4px' }}>({player.team})</span>
            )}
          </div>
          <div style={styles.statsLine}>
            <span style={getStatStyle(stats.seasonPct)}>{stats.seasonPct}%</span>
            {stats.last5Pct !== null && (
              <span style={getStatStyle(stats.last5Pct)}>L5:{stats.last5Pct}%</span>
            )}
            {stats.last10Pct !== null && (
              <span style={getStatStyle(stats.last10Pct)}>L10:{stats.last10Pct}%</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Render a team column
  const renderTeamColumn = (teamAbbrev) => {
    if (!teamAbbrev || activeLine === null) {
      return <div style={styles.noData}>No data</div>;
    }

    // Always filter by the current roster (awayPlayers/homePlayers via /today) so we
    // see how every currently-rostered player performed for the requested season,
    // regardless of who they played for that year. Fall back to the stats team label
    // when no roster map is available (e.g. /today fetch in flight).
    const teamPlayers = currentTeamByPlayerId.size > 0
      ? playersData.filter(p => currentTeamByPlayerId.get(String(p.playerId)) === teamAbbrev)
      : playersData.filter(p => p.team === teamAbbrev);

    if (teamPlayers.length === 0) {
      return <div style={styles.noData}>No players for {teamAbbrev}</div>;
    }

    // Filter to players who have data for this line and sort by season pct
    const playersWithStats = teamPlayers
      .map(p => ({ player: p, stats: getLineStats(p, activeLine) }))
      .filter(({ stats, player }) => {
        if (!stats || stats.seasonPct === null) return false;
        // When Alts is off, only show players that have this line in odds data
        if (!showAlts && Object.keys(oddsLinesByPlayer).length > 0) {
          const playerOddsLines = oddsLinesByPlayer[String(player.playerId)] || [];
          return playerOddsLines.includes(activeLine);
        }
        return true;
      })
      .sort((a, b) => (b.stats.seasonPct || 0) - (a.stats.seasonPct || 0));

    if (playersWithStats.length === 0) {
      return <div style={styles.noData}>No data for line {activeLine}</div>;
    }

    return playersWithStats.map(({ player }, index) =>
      renderPlayerRow(player, index)
    );
  };

  // Define bucket lines for markets that should use buckets (with sub-toggles)
  const bucketLines = {
    'player_points': [5, 10, 15, 20, 25, 30],
    'player_points_rebounds_assists': [20, 25, 30, 35, 40],
    'player_points_rebounds': [15, 20, 25, 30],
    'player_points_assists': [15, 20, 25, 30],
    'player_rebounds_assists': [8, 10, 12, 15]
  };

  // Generate line toggle buttons
  const getDisplayLines = () => {
    // Markets with sub-toggles always use bucket lines
    if (bucketLines[market]) {
      const buckets = bucketLines[market];
      return buckets.filter(l => availableLines.includes(l));
    }

    // When Alts is off, only show lines from odds data
    if (!showAlts && allOddsLines.length > 0) {
      return allOddsLines;
    }

    // For other markets, show all available lines (up to reasonable limit)
    const commonLines = {
      'player_rebounds': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
      'player_assists': [1, 2, 3, 4, 5, 6, 7, 8, 9, 10],
      'player_threes': [1, 2, 3, 4, 5],
      'player_steals': [1, 2, 3],
      'player_blocks': [1, 2, 3],
      'player_turnovers': [1, 2, 3, 4]
    };

    if (availableLines.length <= 10) return availableLines;

    const preferred = commonLines[market] || [];
    return availableLines.filter(l => preferred.includes(l) || l === selectedLine);
  };

  const displayLines = getDisplayLines();

  // For points and combo markets, generate sub-lines when a bucket is selected
  const getSubLines = () => {
    if (!marketsWithSubLines.includes(market) || selectedLine === null) return [];
    // Show individual values from selectedLine to selectedLine + 4
    const subLines = [];
    for (let i = selectedLine; i < selectedLine + 5; i++) {
      if (availableLines.includes(i)) {
        subLines.push(i);
      }
    }
    return subLines;
  };

  const subLines = getSubLines();

  // The actual line to use for data display
  const activeLine = selectedSubLine !== null ? selectedSubLine : selectedLine;

  // Render a team's grid table
  const renderTeamGrid = (teamAbbrev) => {
    if (activeLine === null) {
      return <div style={styles.noData}>Select a line</div>;
    }

    // Always filter by the current roster (awayPlayers/homePlayers via /today) so we
    // see how every currently-rostered player performed for the requested season,
    // regardless of who they played for that year. Fall back to the stats team label
    // when no roster map is available (e.g. /today fetch in flight).
    const teamPlayers = currentTeamByPlayerId.size > 0
      ? playersData.filter(p => currentTeamByPlayerId.get(String(p.playerId)) === teamAbbrev)
      : playersData.filter(p => p.team === teamAbbrev);

    const playersWithStats = teamPlayers
      .map(p => ({ player: p, stats: getLineStats(p, activeLine) }))
      .filter(({ stats, player }) => {
        if (!stats || stats.seasonPct === null) return false;
        // When Alts is off, only show players that have this line in odds data
        if (!showAlts && Object.keys(oddsLinesByPlayer).length > 0) {
          const playerOddsLines = oddsLinesByPlayer[String(player.playerId)] || [];
          return playerOddsLines.includes(activeLine);
        }
        return true;
      })
      .sort((a, b) => (b.stats.seasonPct || 0) - (a.stats.seasonPct || 0));

    if (playersWithStats.length === 0) {
      return <div style={styles.noData}>No data</div>;
    }

    return (
      <table style={styles.gridTable}>
        <thead style={styles.gridHeader}>
          <tr>
            <th style={{ ...styles.gridHeaderCell, textAlign: 'left' }}>Player</th>
            <th style={styles.gridHeaderCell}>All</th>
            <th style={styles.gridHeaderCell}>L5</th>
            <th style={styles.gridHeaderCell}>L10</th>
            <th style={styles.gridHeaderCell}>L20</th>
          </tr>
        </thead>
        <tbody>
          {playersWithStats.map(({ player, stats }, index) => {
            const hasOdds = playerHasOdds(player.playerId);
            const rowStyle = {
              ...styles.gridRow,
              ...(hasOdds ? {} : { opacity: 0.25 })
            };
            return (
              <tr key={player.playerId || index} style={rowStyle}>
                <td style={{ ...styles.gridCell, ...styles.gridCellPlayer, ...(onPlayerClick ? { cursor: 'pointer', textDecoration: 'underline', textDecorationColor: '#6b7280' } : {}) }} onClick={onPlayerClick ? () => onPlayerClick(player.playerId, player.playerName, player.team) : undefined}>
                  {player.playerName}
                  {isPastYear && player.team && currentTeamByPlayerId.get(String(player.playerId)) && player.team !== currentTeamByPlayerId.get(String(player.playerId)) && (
                    <span style={{ color: '#9ca3af', fontWeight: 'normal', marginLeft: '4px' }}>({player.team})</span>
                  )}
                </td>
                <td style={{ ...styles.gridCell, ...getStatStyle(stats.seasonPct) }}>{stats.seasonPct}%</td>
                <td style={{ ...styles.gridCell, ...getStatStyle(stats.last5Pct) }}>{stats.last5Pct ?? '-'}%</td>
                <td style={{ ...styles.gridCell, ...getStatStyle(stats.last10Pct) }}>{stats.last10Pct ?? '-'}%</td>
                <td style={{ ...styles.gridCell, ...getStatStyle(stats.last20Pct) }}>{stats.last20Pct ?? '-'}%</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  // Render grid view with two team tables
  const renderGridView = () => {
    return (
      <div style={styles.columnsContainer}>
        {/* Away Team Grid */}
        <div style={styles.teamColumn}>
          <div style={styles.teamHeader}>
            <img
              style={styles.teamLogo}
              src={`https://a.espncdn.com/i/teamlogos/${sport}/500-dark/${awayTeam?.toLowerCase()}.png`}
              alt={awayTeam}
              onError={(e) => e.target.style.visibility = 'hidden'}
            />
            <span style={styles.teamName}>{awayTeam}</span>
          </div>
          <div style={styles.playersList}>
            {renderTeamGrid(awayTeam)}
          </div>
        </div>

        {/* Home Team Grid */}
        <div style={styles.teamColumn}>
          <div style={styles.teamHeader}>
            <img
              style={styles.teamLogo}
              src={`https://a.espncdn.com/i/teamlogos/${sport}/500-dark/${homeTeam?.toLowerCase()}.png`}
              alt={homeTeam}
              onError={(e) => e.target.style.visibility = 'hidden'}
            />
            <span style={styles.teamName}>{homeTeam}</span>
          </div>
          <div style={styles.playersList}>
            {renderTeamGrid(homeTeam)}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <h2 style={styles.title}>{config.label.toUpperCase()}</h2>
        </div>
        <div style={styles.headerCenter}>
          <div className="form-check form-switch mb-0" style={{ paddingLeft: '0', position: 'relative' }}>
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="alts-toggle"
              checked={showAlts}
              onChange={() => setShowAlts(!showAlts)}
              style={{ width: '4em', height: '1.3em', minWidth: '4em', cursor: 'pointer' }}
            />
            <label
              className="form-check-label text-white"
              htmlFor="alts-toggle"
              style={{
                position: 'absolute', left: '2.5em', top: '50%', transform: 'translateY(-50%)',
                fontSize: '8px', fontWeight: 'bold', opacity: 0.8,
                pointerEvents: 'none', userSelect: 'none'
              }}
            >
              Alts
            </label>
          </div>
          <button
            style={{
              ...styles.viewToggle,
              ...(viewMode === 'tiles' ? styles.viewToggleActive : {})
            }}
            onClick={() => setViewMode('tiles')}
          >
            Tiles
          </button>
          <button
            style={{
              ...styles.viewToggle,
              ...(viewMode === 'grid' ? styles.viewToggleActive : {})
            }}
            onClick={() => setViewMode('grid')}
          >
            Grid
          </button>
        </div>
        <div style={styles.headerRight}></div>
      </div>

      {/* Line Toggles */}
      <div style={styles.toggleContainer}>
        {displayLines.map(line => (
          <button
            key={line}
            style={{
              ...styles.lineToggle,
              ...(selectedLine === line ? styles.lineToggleActive : {})
            }}
            onClick={() => setSelectedLine(line)}
          >
            {line}+
          </button>
        ))}
      </div>

      {/* Sub-line Toggles for Points and Combo Markets */}
      {marketsWithSubLines.includes(market) && subLines.length > 0 && (
        <div style={styles.toggleContainer}>
          {subLines.map(line => (
            <button
              key={line}
              style={{
                ...styles.lineToggle,
                ...(selectedSubLine === line ? styles.viewToggleActive : {})
              }}
              onClick={() => setSelectedSubLine(line)}
            >
              {line}
            </button>
          ))}
        </div>
      )}

      {/* Defense Data */}
      {showDefense && (
        <div style={{ marginBottom: '0.5rem' }}>
          {defenseLoading && (
            <div style={{ textAlign: 'center', color: '#888', fontSize: '0.75rem', padding: '0.5rem' }}>Loading defense data...</div>
          )}
          {!defenseLoading && Object.keys(defenseData).length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              {[awayTeam, homeTeam].map(team => {
                const teamDef = defenseData[team];
                if (!teamDef) return null;

                const defKeys = defenseKeyMap[market];
                const marketDef = defKeys ? combineDefenseStats(teamDef, defKeys) : null;
                if (!marketDef) return (
                  <div key={team} style={{ flex: 1, background: '#1a1a1a', borderRadius: '0.35rem', padding: '0.5rem', border: '1px solid #333', textAlign: 'center' }}>
                    <span style={{ fontSize: '0.7rem', color: '#666' }}>No defense data for this market</span>
                  </div>
                );

                const overall = marketDef.overall || {};
                const home = marketDef.home || {};
                const away = marketDef.away || {};
                const b2b = marketDef.back_to_back || {};
                const games = marketDef.games || [];

                // Last 5 games with actual data (value > 0)
                const recentGames = games.filter(g => g.topPlayer).slice(-5).reverse();

                return (
                  <div key={team} style={{ flex: 1, background: '#1a1a1a', borderRadius: '0.35rem', padding: '0.5rem', border: '1px solid #333' }}>
                    {/* Team header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem', marginBottom: '0.4rem' }}>
                      <img
                        style={{ width: '20px', height: '20px', objectFit: 'contain' }}
                        src={`https://a.espncdn.com/i/teamlogos/${sport}/500-dark/${team?.toLowerCase()}.png`}
                        alt={team}
                        onError={(e) => e.target.style.visibility = 'hidden'}
                      />
                      <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#e74c3c' }}>{team} DEF</span>
                    </div>

                    {/* Averages row */}
                    <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
                      {[
                        { label: 'ALL', val: overall.avg, gp: overall.gamesPlayed },
                        { label: 'HM', val: home.avg, gp: home.gamesPlayed },
                        { label: 'AW', val: away.avg, gp: away.gamesPlayed },
                        { label: 'B2B', val: b2b.avg, gp: b2b.gamesPlayed },
                      ].map(({ label, val, gp }) => (
                        <div key={label} style={{ background: '#222', borderRadius: '0.2rem', padding: '0.2rem 0.35rem', textAlign: 'center', minWidth: '42px' }}>
                          <div style={{ fontSize: '0.55rem', color: '#888', fontWeight: '600' }}>{label}</div>
                          <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#fff' }}>{val != null ? val.toFixed(1) : '-'}</div>
                          <div style={{ fontSize: '0.5rem', color: '#666' }}>{gp || 0}g</div>
                        </div>
                      ))}
                    </div>

                    {/* Last 5 games */}
                    {recentGames.length > 0 && (
                      <div>
                        <div style={{ fontSize: '0.6rem', color: '#888', fontWeight: '600', marginBottom: '0.2rem' }}>LAST {recentGames.length}</div>
                        {recentGames.map((g, i) => {
                          const isCombo = (defKeys?.length || 0) > 1;
                          return (
                            <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '0.15rem 0.25rem', borderBottom: '1px solid #282828', fontSize: '0.65rem' }}>
                              <span style={{ width: '25%', flexShrink: 0, color: '#888' }}>
                                {g.isHome ? 'vs' : '@'} {g.opponent}
                              </span>
                              {!isCombo && (
                                <span style={{ width: '15%', flexShrink: 0, fontWeight: 'bold', color: '#fff', textAlign: 'center' }}>{g.value}</span>
                              )}
                              <span style={{ width: isCombo ? '75%' : '60%', color: '#f39c12', fontSize: '0.6rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'right' }}>
                                {g.topPlayer ? `${g.topPlayer.playerName} ${g.topPlayer.value}` : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Grid View */}
      {viewMode === 'grid' && renderGridView()}

      {/* Two Column Layout (Tiles) */}
      {viewMode === 'tiles' && (
      <div style={styles.columnsContainer}>
        {/* Away Team Column */}
        <div style={styles.teamColumn}>
          <div style={styles.teamHeader}>
            <img
              style={styles.teamLogo}
              src={`https://a.espncdn.com/i/teamlogos/${sport}/500-dark/${awayTeam?.toLowerCase()}.png`}
              alt={awayTeam}
              onError={(e) => e.target.style.visibility = 'hidden'}
            />
            <span style={styles.teamName}>{awayTeam}</span>
          </div>
          <div style={styles.playersList}>
            {renderTeamColumn(awayTeam)}
          </div>
        </div>

        {/* Home Team Column */}
        <div style={styles.teamColumn}>
          <div style={styles.teamHeader}>
            <img
              style={styles.teamLogo}
              src={`https://a.espncdn.com/i/teamlogos/${sport}/500-dark/${homeTeam?.toLowerCase()}.png`}
              alt={homeTeam}
              onError={(e) => e.target.style.visibility = 'hidden'}
            />
            <span style={styles.teamName}>{homeTeam}</span>
          </div>
          <div style={styles.playersList}>
            {renderTeamColumn(homeTeam)}
          </div>
        </div>
      </div>
      )}
    </div>
  );
};

export default TrendsPropReport;
